import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList,
  Loader2, RefreshCw, ShieldAlert,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Route & Dates' },
  { id: 3, label: 'Dry-run Review' },
  { id: 4, label: 'Apply' },
];

const ROUTE_INFO = {
  A: {
    label: 'Route A — Open Draft',
    summary: 'Seeds a new Draft audit from Ref 066 closing (18 Aug 2026). period_to = NULL (open-ended).',
    periodToLabel: 'None (open-ended)',
    periodToFixed: true,
  },
  B: {
    label: 'Route B — Historical Draft',
    summary: 'Creates historical Ref 067 Draft for 18 Aug – 25 Sep 2026. period_to = 25 Sep 2026.',
    periodToLabel: 'Provided (25 Sep 2026)',
    periodToFixed: false,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v) {
  if (v == null) return '—';
  return parseFloat(v).toFixed(4);
}

function fmtGBP(v) {
  if (v == null) return '—';
  return `£${parseFloat(v).toFixed(2)}`;
}

function StatusBadge({ status }) {
  const cls = {
    planned: 'bg-amber-50 text-amber-700 border-amber-200',
    applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    importable: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    held: 'bg-red-50 text-red-700 border-red-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <Badge variant="outline" className={`text-xs font-medium ${cls}`}>
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function BridgePanel({ bridge }) {
  if (!bridge) return null;
  const reconciles = bridge.reconciles === true || bridge.reconciles === 'true';
  return (
    <div
      data-testid="valuation-bridge"
      className={`rounded-lg border p-4 ${reconciles ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-slate-700">Valuation Bridge</span>
        {reconciles
          ? <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">Reconciles</Badge>
          : <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">Mismatch</Badge>
        }
      </div>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-xs text-slate-500 mb-1">Source Total</div>
          <div className="text-lg font-bold text-slate-800" data-testid="bridge-source-total">
            {fmtGBP(bridge.source_total)}
          </div>
          <div className="text-xs text-slate-400">OPENCOST sum</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Importable</div>
          <div className="text-lg font-bold text-emerald-700" data-testid="bridge-importable-total">
            {fmtGBP(bridge.importable_total)}
          </div>
          <div className="text-xs text-slate-400">{bridge.importable_count} rows</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Held (Exceptions)</div>
          <div className="text-lg font-bold text-red-700" data-testid="bridge-held-total">
            {fmtGBP(bridge.held_total)}
          </div>
          <div className="text-xs text-slate-400">{bridge.held_count} rows</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500 text-center">
        Importable {fmtGBP(bridge.importable_total)} + Held {fmtGBP(bridge.held_total)} = {fmtGBP((parseFloat(bridge.importable_total || 0) + parseFloat(bridge.held_total || 0)).toFixed(2))}
        {reconciles ? ' ✓' : ' ← does not match source total'}
      </div>
    </div>
  );
}

function SourceSummary({ s1Run, route, sessionRef, sessionDate, periodFrom, periodTo }) {
  if (!s1Run) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 mb-4" data-testid="source-summary">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <span><span className="font-semibold">Source:</span> {s1Run.source_house || s1Run.source_internal_name} ({s1Run.run_id?.slice(0, 8)})</span>
        <span><span className="font-semibold">Route:</span> {route ? `${route} — ${ROUTE_INFO[route]?.label}` : '—'}</span>
        <span><span className="font-semibold">Reference:</span> {sessionRef || '—'}</span>
        <span><span className="font-semibold">Session Date:</span> {sessionDate || '—'}</span>
        <span><span className="font-semibold">Period From:</span> {periodFrom || '—'}</span>
        <span><span className="font-semibold">Period To:</span> {periodTo || '(None — open draft)'}</span>
      </div>
    </div>
  );
}

// ── Opening rows table ────────────────────────────────────────────────────────

function OpeningRowsTable({ planId, category, setCategory }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 25;

  const load = useCallback(async (cat, pg) => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage2/plans/${planId}?category=${cat}&page=${pg}&page_size=${PAGE_SIZE}`
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load opening rows');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    setPage(1);
    load(category, 1);
  }, [category, planId, load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="opening-rows-table">
      {/* Category tabs */}
      <div className="flex gap-2 mb-3">
        {['importable', 'held', 'all'].map(cat => (
          <button
            key={cat}
            data-testid={`rows-tab-${cat}`}
            onClick={() => { setCategory(cat); }}
            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${category === cat ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
        {loading && <Loader2 size={14} className="animate-spin text-slate-400 self-center ml-1" />}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wide">
              <th className="py-2 px-3 font-semibold">Code</th>
              <th className="py-2 px-3 font-semibold">Description</th>
              <th className="py-2 px-3 font-semibold">Location</th>
              <th className="py-2 px-3 font-semibold text-right">Open Qty</th>
              <th className="py-2 px-3 font-semibold text-right">Open Cost (£)</th>
              <th className="py-2 px-3 font-semibold text-right">WAC</th>
              <th className="py-2 px-3 font-semibold">Matched Product</th>
              <th className="py-2 px-3 font-semibold">Status</th>
              {category === 'held' && <th className="py-2 px-3 font-semibold">Hold Reason</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan="9" className="py-8 text-center text-slate-400">No rows</td>
              </tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`opening-row-${r.id}`}>
                <td className="py-1.5 px-3 font-mono text-slate-700">{r.legacy_item_code}</td>
                <td className="py-1.5 px-3 text-slate-600 max-w-[200px] truncate">{r.source_description || '—'}</td>
                <td className="py-1.5 px-3 text-slate-500">{r.source_location || '—'}</td>
                <td className="py-1.5 px-3 text-right text-slate-700">{fmt(r.source_open)}</td>
                <td className="py-1.5 px-3 text-right font-medium text-slate-800">{fmtGBP(r.source_opencost)}</td>
                <td className="py-1.5 px-3 text-right text-slate-600">{r.source_wac != null ? fmtGBP(r.source_wac) : '—'}</td>
                <td className="py-1.5 px-3 text-slate-600">{r.bp_name_snapshot || '—'}</td>
                <td className="py-1.5 px-3"><StatusBadge status={r.resolution_status} /></td>
                {category === 'held' && (
                  <td className="py-1.5 px-3 text-slate-500 text-xs">{r.hold_reason || '—'}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{total} total rows</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => { const p = page - 1; setPage(p); load(category, p); }}>
              Prev
            </Button>
            <span className="self-center">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(category, p); }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PsbStage2Page() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const restoredRef = useRef(false);  // guard against double-restore

  // ── Wizard state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // Step 1 — source
  const [s1Runs, setS1Runs] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(true);

  // Step 2 — route & dates
  const [route, setRoute] = useState('');
  const [sessionRef, setSessionRef] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  // Step 3 — plan / dry-run
  const [planId, setPlanId] = useState('');
  const [planData, setPlanData] = useState(null);
  const [rowCategory, setRowCategory] = useState('importable');
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Step 4 — apply
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Environment banner
  const [envLabel, setEnvLabel] = useState('');
  const [envColor, setEnvColor] = useState('');

  // ── Single serialised boot: env → runs → (optional) plan restore ──────────
  useEffect(() => {
    api.get('/admin/product-master/environment')
      .then(r => { setEnvLabel(r.data.label || ''); setEnvColor(r.data.color || 'amber'); })
      .catch(() => {});
    bootLoad();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bootLoad = async () => {
    // 1. Load s1 runs first so DDL lock (_ensure_s2_tables) settles before any plan fetch
    let runs = [];
    setLoadingRuns(true);
    try {
      const res = await api.get('/admin/psb-stage2/s1-runs');
      runs = res.data.runs || [];
      setS1Runs(runs);
    } catch (e) {
      toast.error('Failed to load Stage 1 runs');
    } finally {
      setLoadingRuns(false);
    }

    // 2. Restore plan if planId is in query params (only once per mount)
    if (restoredRef.current) return;
    const pid = searchParams.get('planId');
    if (pid) {
      restoredRef.current = true;
      await restorePlan(pid, runs);
    }
  };

  const restorePlan = async (pid, runsHint) => {
    try {
      const res = await api.get(`/admin/psb-stage2/plans/${pid}?category=importable&page=1`);
      const p = res.data.plan;
      if (!p) { toast.error('Plan not found — URL may be stale'); return; }

      setPlanId(pid);
      setPlanData(p);
      setSelectedRunId(p.s1_run_id);
      setRoute(p.route);
      setSessionRef(p.session_reference);
      setSessionDate(p.session_date);
      setPeriodFrom(p.period_from || '');
      setPeriodTo(p.period_to || '');

      // Use already-loaded runs list to find the run object
      const pool = runsHint || s1Runs;
      const matchedRun = pool.find(r => r.run_id === p.s1_run_id);
      if (matchedRun) setSelectedRun(matchedRun);

      if (p.status === 'applied') {
        setStep(4);
        setApplyResult({
          idempotent: true,
          plan_id: pid,
          session: {
            id: p.created_session_id,
            session_reference: p.session_reference,
            session_date: p.session_date,
            status: 'draft',
            period_to: p.period_to ?? null,
          },
          route: p.route,
          bridge: p.bridge,
          applied_at: p.applied_at,
          inserted_opening_rows: 0,
        });
      } else {
        setStep(3);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Could not restore plan: ${msg}`);
    }
  };

  // Persist plan ID in URL query param (React Router-managed)
  const setPlanIdAndUrl = (pid) => {
    setPlanId(pid);
    setSearchParams({ planId: pid }, { replace: true });
  };

  const loadS1Runs = async () => {
    setLoadingRuns(true);
    try {
      const res = await api.get('/admin/psb-stage2/s1-runs');
      setS1Runs(res.data.runs || []);
    } catch (e) {
      toast.error('Failed to load Stage 1 runs');
    } finally {
      setLoadingRuns(false);
    }
  };

  // ── Step 1: source selection ──────────────────────────────────────────────
  const handleSelectRun = (runId) => {
    setSelectedRunId(runId);
    setSelectedRun(s1Runs.find(r => r.run_id === runId) || null);
  };

  const goStep2 = () => {
    if (!selectedRunId) { toast.error('Select a Stage 1 run first'); return; }
    setStep(2);
  };

  // ── Step 2: route & dates ─────────────────────────────────────────────────
  const handleRouteChange = (r) => {
    setRoute(r);
    if (r === 'A') {
      // Route A defaults
      setSessionDate('2026-08-18');
      setPeriodFrom('');
      setPeriodTo('');    // NULL — enforced
    } else if (r === 'B') {
      // Route B defaults
      setSessionDate('2026-09-25');
      setPeriodFrom('2026-08-18');
      setPeriodTo('2026-09-25');
    }
  };

  const goStep3 = async () => {
    if (!route) { toast.error('Select a route'); return; }
    if (!sessionRef.trim()) { toast.error('Session reference required'); return; }
    if (!sessionDate) { toast.error('Session date required'); return; }

    setCreatingPlan(true);
    try {
      const body = {
        s1_run_id: selectedRunId,
        route,
        session_reference: sessionRef.trim(),
        session_date: sessionDate,
        period_from: periodFrom || null,
        period_to: route === 'A' ? null : (periodTo || null),
        notes: `PSB Stage 2 Route ${route} — created via UI`,
      };
      const res = await api.post('/admin/psb-stage2/plan', body);
      const plan = res.data.plan;
      setPlanData(plan);
      setPlanIdAndUrl(plan.run_id);
      setStep(3);
      toast.success('Dry-run plan created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally {
      setCreatingPlan(false);
    }
  };

  // ── Step 4: apply ─────────────────────────────────────────────────────────
  const handleApply = async () => {
    if (!planId) return;
    setApplying(true);
    try {
      const res = await api.post(`/admin/psb-stage2/plans/${planId}/apply`);
      setApplyResult(res.data);
      // Reload plan for updated status
      const planRes = await api.get(`/admin/psb-stage2/plans/${planId}?category=importable&page=1`);
      setPlanData(planRes.data.plan);
      setStep(4);
      toast.success(res.data.idempotent ? 'Idempotent apply — existing session returned' : `Draft session created (${res.data.inserted_opening_rows} opening rows)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="psb-stage2-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/stocktaking/psb-stage1-import')}
          data-testid="back-to-stage1"
          className="text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={16} className="mr-1" /> Stage 1
        </Button>
        <span className="text-slate-300">/</span>
        <h1 className="text-xl font-semibold text-slate-900">PSB Stage 2 — Session Shell &amp; Opening Stock</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4 ml-[72px]">
        Leicester V32 · Fixture site 271 · Preview only · Draft sessions only
      </p>

      {/* Environment banner */}
      {envLabel && (
        <div
          data-testid="env-banner"
          className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${envColor === 'red' ? 'border-red-300 bg-red-50 text-red-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
        >
          <ShieldAlert size={15} />
          {envLabel} — Stage 2 writes are gated to Preview / fixture site only.
        </div>
      )}

      <StepNav current={step} />

      {/* ── Step 1: Source ──────────────────────────────────────────────── */}
      {step === 1 && (
        <Card data-testid="step-1-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList size={16} />
              Select Stage 1 Source Run
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={loadS1Runs} disabled={loadingRuns} data-testid="refresh-runs-btn">
                <RefreshCw size={14} className={loadingRuns ? 'animate-spin' : ''} />
              </Button>
              <span className="text-xs text-slate-500">{s1Runs.length} run(s) available</span>
            </div>

            {s1Runs.length === 0 && !loadingRuns && (
              <div className="text-sm text-slate-400 py-4 text-center">
                No applied Stage 1 runs found for the allowed fixture site.
              </div>
            )}

            <div className="space-y-2">
              {s1Runs.map(run => (
                <div
                  key={run.run_id}
                  data-testid={`s1-run-row-${run.run_id.slice(0, 8)}`}
                  onClick={() => handleSelectRun(run.run_id)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${selectedRunId === run.run_id ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-sm text-slate-800">{run.source_house || run.source_internal_name}</span>
                      <span className="text-xs text-slate-400 ml-2">run {run.run_id?.slice(0, 8)}</span>
                    </div>
                    <StatusBadge status={run.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Site {run.target_site_id} · {run.match_count} product matches
                    {run.applied_at && ` · Applied ${run.applied_at.slice(0, 10)}`}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={goStep2}
                disabled={!selectedRunId}
                data-testid="next-to-step2-btn"
              >
                Next: Route &amp; Dates <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Route & Dates ───────────────────────────────────────── */}
      {step === 2 && (
        <Card data-testid="step-2-card">
          <CardHeader>
            <CardTitle className="text-base">Route &amp; Session Dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <SourceSummary s1Run={selectedRun} route={route} sessionRef={sessionRef} sessionDate={sessionDate} periodFrom={periodFrom} periodTo={periodTo} />

            {/* Route selector */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Route</Label>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(ROUTE_INFO).map(([key, info]) => (
                  <div
                    key={key}
                    data-testid={`route-option-${key}`}
                    onClick={() => handleRouteChange(key)}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${route === key ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className="font-semibold text-sm text-slate-800 mb-1">{info.label}</div>
                    <div className="text-xs text-slate-500">{info.summary}</div>
                    <div className="mt-2 text-xs">
                      <span className="font-medium text-slate-600">period_to:</span>{' '}
                      <span className={key === 'A' ? 'text-amber-600 font-medium' : 'text-slate-600'}>{info.periodToLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Session reference */}
            <div className="space-y-1">
              <Label htmlFor="session-ref">Session Reference</Label>
              <Input
                id="session-ref"
                data-testid="session-ref-input"
                placeholder="e.g. Ref 067"
                value={sessionRef}
                onChange={e => setSessionRef(e.target.value)}
              />
            </div>

            {/* Session date */}
            <div className="space-y-1">
              <Label htmlFor="session-date">Session Date</Label>
              <Input
                id="session-date"
                data-testid="session-date-input"
                type="date"
                value={sessionDate}
                onChange={e => setSessionDate(e.target.value)}
              />
            </div>

            {/* Period from */}
            <div className="space-y-1">
              <Label htmlFor="period-from">Period From <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
              <Input
                id="period-from"
                data-testid="period-from-input"
                type="date"
                value={periodFrom}
                onChange={e => setPeriodFrom(e.target.value)}
              />
            </div>

            {/* Period to */}
            <div className="space-y-1">
              <Label htmlFor="period-to">
                Period To{' '}
                {route === 'A'
                  ? <span className="text-amber-600 text-xs font-semibold">(Forced NULL for Route A)</span>
                  : <span className="text-slate-400 font-normal text-xs">(optional)</span>
                }
              </Label>
              <Input
                id="period-to"
                data-testid="period-to-input"
                type="date"
                value={route === 'A' ? '' : periodTo}
                onChange={e => { if (route !== 'A') setPeriodTo(e.target.value); }}
                disabled={route === 'A'}
                className={route === 'A' ? 'opacity-50 cursor-not-allowed' : ''}
                placeholder={route === 'A' ? 'NULL (open draft)' : ''}
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-step1-btn">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={goStep3}
                disabled={!route || !sessionRef.trim() || !sessionDate || creatingPlan}
                data-testid="create-plan-btn"
              >
                {creatingPlan ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Create Dry-run Plan <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 3: Dry-run Review ─────────────────────────────────────── */}
      {step === 3 && planData && (
        <Card data-testid="step-3-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Dry-run Plan Review</span>
              <StatusBadge status={planData.status} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SourceSummary
              s1Run={selectedRun}
              route={planData.route}
              sessionRef={planData.session_reference}
              sessionDate={planData.session_date}
              periodFrom={planData.period_from}
              periodTo={planData.period_to}
            />

            {/* Plan metadata */}
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg border p-3 flex flex-wrap gap-x-6 gap-y-1">
              <span><span className="font-medium">Plan ID:</span> <span className="font-mono" data-testid="plan-id">{planId}</span></span>
              <span><span className="font-medium">Created:</span> {planData.created_at?.slice(0, 16)}</span>
              {planData.applied_at && (
                <span><span className="font-medium">Applied:</span> {planData.applied_at?.slice(0, 16)}</span>
              )}
            </div>

            <BridgePanel bridge={planData.bridge} />

            <OpeningRowsTable
              planId={planId}
              category={rowCategory}
              setCategory={setRowCategory}
            />

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-step2-btn">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={handleApply}
                disabled={applying || planData.status === 'applied'}
                data-testid="apply-btn"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                {applying ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {planData.status === 'applied' ? 'Already Applied' : 'Apply — Create Draft Session'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 4: Apply result ───────────────────────────────────────── */}
      {step === 4 && (
        <Card data-testid="step-4-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" />
              {applyResult?.idempotent ? 'Idempotent — Existing Session' : 'Draft Session Created'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {applyResult && (
              <>
                <SourceSummary
                  s1Run={selectedRun}
                  route={applyResult.route || planData?.route}
                  sessionRef={planData?.session_reference}
                  sessionDate={planData?.session_date}
                  periodFrom={planData?.period_from}
                  periodTo={planData?.period_to}
                />

                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
                  {applyResult.session && (
                    <>
                      <div className="text-sm font-semibold text-emerald-800">Session Created</div>
                      <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-6 gap-y-1">
                        <span><span className="font-medium">Session ID:</span> <span className="font-mono" data-testid="created-session-id">{applyResult.session.id}</span></span>
                        <span><span className="font-medium">Reference:</span> {applyResult.session.session_reference}</span>
                        <span><span className="font-medium">Date:</span> {applyResult.session.session_date}</span>
                        <span><span className="font-medium">Status:</span> {applyResult.session.status}</span>
                        <span><span className="font-medium">period_to:</span> {applyResult.session.period_to ?? 'NULL'}</span>
                      </div>
                    </>
                  )}
                  <div className="text-xs text-slate-600 pt-1">
                    <span className="font-medium">Opening rows inserted:</span>{' '}
                    <span data-testid="inserted-rows-count">{applyResult.idempotent ? '0 (idempotent — existing session returned)' : applyResult.inserted_opening_rows}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    Applied at: {applyResult.applied_at?.slice(0, 16)}
                  </div>
                </div>

                {planData?.bridge && <BridgePanel bridge={planData.bridge} />}

                {/* Idempotency note */}
                {applyResult.idempotent && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="idempotent-notice">
                    Idempotency gate: this plan was already applied. The existing Draft session is returned unchanged — no duplicate rows created.
                  </div>
                )}

                {/* Re-apply for idempotency proof */}
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(3)} data-testid="back-to-step3-btn">
                    <ArrowLeft size={14} className="mr-1" /> Back to Plan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleApply}
                    disabled={applying}
                    data-testid="repeat-apply-btn"
                    className="text-slate-600"
                  >
                    {applying ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                    Re-apply (Idempotency Test)
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
